import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
  OnInit,
  output,
  Signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TuiChip } from '@taiga-ui/kit';
import { TuiRadio } from '@taiga-ui/core';
import { RequestStatusesService } from '@core/services/request/request-statuses.service';
import {
  BehaviorSubject,
  EMPTY,
  filter,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { RequestStatus, RequestStatuses } from '@core/types/RequestStatus';
import { Nullable } from '@core/helpers/nullable';
import { assertNever } from '@core/helpers/assertNever';
import {
  RequestStatusChangeReasonForm,
  RequestStatusDealDetailsForm,
} from '../../types';
import { RequestService } from '@core/services/request/request.service';
import { AsyncPipe, NgClass } from '@angular/common';
import { DeviceDetectorService, IsLoadingService } from '@core/services';
import { TranslatePipe } from '@ngx-translate/core';
import { hexToRgba } from '@core/helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'x-request-form-statuses-list',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RequestFormStatusesListComponent),
      multi: true,
    },
  ],
  imports: [TuiRadio, FormsModule, AsyncPipe, TranslatePipe, TuiChip, NgClass],
  templateUrl: './request-form-statuses-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormStatusesListComponent
  implements ControlValueAccessor, OnInit
{
  private readonly requestService = inject(RequestService);
  private readonly requestStatusesService = inject(RequestStatusesService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  readonly deviceService = inject(DeviceDetectorService);

  $statusChangeInProcess = output<boolean>({
    alias: 'statusChangeInProcess',
  });

  private onTouched!: (value: any) => void;
  private onChange!: (value: any) => void;
  readonly valueChanged$ = new Subject<RequestStatus>();
  readonly $request = this.requestService.$request;
  readonly $requestStatuses: Signal<RequestStatus[]> =
    this.requestStatusesService.$entities;
  // Заказчику недоступна смена статуса выполненных/выданных заявок (SYST-3029):
  // показываем текущий статус и статусы, в которые переход разрешён
  readonly $visibleStatuses: Signal<RequestStatus[]> = computed(() => {
    const currentStatus = this.$request().statusId;

    return this.$requestStatuses().filter(
      status =>
        status.id === currentStatus?.id ||
        !this.requestStatusesService.isStatusChangeForbidden(
          currentStatus?.id,
          status.id,
        ),
    );
  });

  value: Nullable<RequestStatus> = null;
  prevValue: Nullable<RequestStatus> = null;

  $insideDropdown = input<boolean>(false, { alias: 'insideDropdown' });

  $errorHappened$ = input.required<Subject<void>>({ alias: 'errorHappened' });
  $readOnly = input<boolean>(false, { alias: 'readOnly' });

  constructor() {
    effect(() => {
      if (this.isStatusChanged) {
        this.value = this.$request().statusId;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngOnInit() {
    this.valueChanged$
      .pipe(
        filter(status => status?.id !== this.$request().statusId?.id),
        tap(() => {
          this.$statusChangeInProcess.emit(true);
        }),
        switchMap(status => this.tryToChange$(status).pipe(take(1))),
        switchMap(this.checkFormData$.bind(this)),
        tap(this.valueChanged.bind(this)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // revert old status in UI if error happened and status was not changed
    this.$errorHappened$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.writeValue(this.prevValue);
        this.changeDetectorRef.markForCheck();
      });
  }

  private tryToChange$(
    status: RequestStatus,
  ): Observable<
    | Nullable<RequestStatusChangeReasonForm | RequestStatusDealDetailsForm>
    | boolean
  > {
    switch (status.id) {
      case RequestStatuses.EditQuery:
        return this.requestStatusesService.openEditDialog$();
      case RequestStatuses.Completed:
        return this.requestStatusesService.tryToChangeCompletedStatus$(
          status,
          this.revertOldStatus$.bind(this),
        );
      case RequestStatuses.Given:
        return this.requestStatusesService.tryToChangeGivenStatus$(
          status,
          this.revertOldStatus$.bind(this),
        );
      case RequestStatuses.Cancelled:
        return this.requestStatusesService.openCancelDialog$();
      case RequestStatuses.New:
      case RequestStatuses.OnEdit:
      case RequestStatuses.CheckFinished:
      case RequestStatuses.RequestIsWaitingForCheck:
      case RequestStatuses.InWork:
      case RequestStatuses.SLSWasAppointed:
      case RequestStatuses.MistakeInRequest:
      case RequestStatuses.RejectedLocal:
      case RequestStatuses.Rejected:
        return this.requestStatusesService.tryToChangeAnyOtherStatus$(
          status,
          this.revertOldStatus$.bind(this),
        );
      default:
        assertNever(status.id);
    }
  }

  private checkFormData$(
    formData:
      | Nullable<RequestStatusChangeReasonForm | RequestStatusDealDetailsForm>
      | boolean,
  ): Observable<RequestStatus> {
    if (!formData || !this.value) {
      return this.revertOldStatus$();
    }

    return of({
      ...this.value,
      formData: typeof formData === 'object' ? formData : undefined,
    });
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  writeValue(value: Nullable<RequestStatus>) {
    this.value = value;
    this.prevValue = value;
  }

  valueChanged(value: Nullable<RequestStatus>) {
    this.onChange?.(value);
    this.onTouched?.(value);
  }

  private revertOldStatus$(): Observable<never> {
    this.writeValue(this.prevValue);
    this.changeDetectorRef.detectChanges();
    this.$statusChangeInProcess.emit(false);

    return EMPTY;
  }

  private get isStatusChanged(): boolean {
    const { statusId } = this.$request();

    return statusId?.id !== this.value?.id;
  }

  handleClick(status: RequestStatus): void {
    this.prevValue = this.value;
    this.value = status;
    this.valueChanged$.next(status);
  }

  protected readonly hexToRgba = hexToRgba;
}
